// The pivot's Customize panel — a port of Brands Manager's
// reports/components/CustomizePanel.jsx into the V2 idiom.
//
// Same shape as BM's, because a reader who has customised a report there should
// not have to learn a second way here: a titled sheet, a search box, two tabs
// (Breakdowns / Measures), grouped sections, a live selected-count, and a
// footnote about what the figures are. What differs is only the styling system
// — BM is Tailwind, V2 is CSS Modules.
//
// COMPACT, AND THE EXPLANATIONS LIVE IN TOOLTIPS. The first cut printed every
// hint as a second line under its label, which turned nine choices into a
// scrolling wall and buried the two that matter. One line per option, with an
// info dot carrying the full sentence, fits the whole vocabulary in the frame —
// which is the point of a panel that sits beside the table rather than over it.
//
// Two behaviours are load-bearing rather than cosmetic:
//
//   BREAKDOWN ORDER IS SHOWN AND IS MEANINGFUL. Breakdowns nest, so
//   campaign-then-month is a different table from month-then-campaign. The
//   badge is the position, not a count.
//
//   THE LAST AXIS CANNOT BE REMOVED. A pivot with no breakdown is not an empty
//   pivot, it is a single unlabelled row — so the final checkbox is disabled
//   rather than allowed to produce a table nobody asked for.

import React, { useState, useMemo } from 'react';
import { X, Search, Info } from 'lucide-react';
import s from './CustomizePanel.module.css';

const TABS = [
  { id: 'breakdowns', label: 'Breakdowns' },
  { id: 'measures', label: 'Measures' },
];

export default function CustomizePanel({
  tab, onTab, dimensions, measures, selectedBreakdowns, selectedMeasures,
  onToggleBreakdown, onToggleMeasure, onClose, maxBreakdowns, help,
  dimensionSections, measureSections, footNote,
}) {
  const [q, setQ] = useState('');
  const isBreak = tab === 'breakdowns';
  const items = isBreak ? dimensions : measures;
  const selected = isBreak ? selectedBreakdowns : selectedMeasures;
  const sections = isBreak ? dimensionSections : measureSections;

  // Sections are a presentation of the vocabulary, never a filter on it: an
  // option the server offers but no section lists would silently vanish, so
  // whatever is left over gets its own group rather than disappearing.
  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (i) => !needle
      || i.label.toLowerCase().includes(needle)
      || i.key.toLowerCase().includes(needle)
      || (help[i.key] || '').toLowerCase().includes(needle);
    const byKey = new Map(items.map((i) => [i.key, i]));
    const placed = new Set();
    const out = [];
    for (const sec of sections || []) {
      const list = sec.keys.map((k) => byKey.get(k)).filter(Boolean).filter(match);
      sec.keys.forEach((k) => { if (byKey.has(k)) placed.add(k); });
      if (list.length) out.push({ id: sec.id, label: sec.label, items: list });
    }
    const rest = items.filter((i) => !placed.has(i.key)).filter(match);
    if (rest.length) out.push({ id: 'other', label: 'Other', items: rest });
    return out;
  }, [items, sections, q, help]);

  const atCap = isBreak && selected.length >= maxBreakdowns;
  const shown = grouped.reduce((t, g) => t + g.items.length, 0);

  return (
    <aside className={s.panel} aria-label="Customize pivot table">
      <div className={s.head}>
        <h3 className={s.title}>Customize</h3>
        <button type="button" className={s.close} onClick={onClose} aria-label="Close">
          <X size={14} />
        </button>
      </div>

      <div className={s.searchWrap}>
        <Search size={13} className={s.searchIcon} aria-hidden="true" />
        <input className={s.search} value={q} onChange={(e) => setQ(e.target.value)}
               placeholder={isBreak ? 'Search breakdowns' : 'Search measures'}
               aria-label={isBreak ? 'Search breakdowns' : 'Search measures'} />
      </div>

      <div className={s.tabs} role="tablist" aria-label="Customize sections">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                  className={`${s.tab} ${tab === t.id ? s.tabOn : ''}`}
                  onClick={() => { onTab(t.id); setQ(''); }}>
            {t.label}
            <span className={s.tabCount}>
              {t.id === 'breakdowns' ? selectedBreakdowns.length : selectedMeasures.length}
            </span>
          </button>
        ))}
      </div>

      <div className={s.count}>
        {isBreak
          ? `${selected.length}/${maxBreakdowns} · order sets the nesting`
          : `${selected.length} columns shown`}
      </div>

      <div className={s.list}>
        {!shown && <p className={s.none}>Nothing matches &ldquo;{q}&rdquo;.</p>}
        {grouped.map((g) => (
          <div key={g.id} className={s.section}>
            <h4 className={s.sectionHead}>{g.label}</h4>
            {g.items.map((i) => {
              const on = selected.includes(i.key);
              // At the cap, unchecked rows are disabled rather than silently
              // ignored — a checkbox that does nothing when clicked is a worse
              // answer than one that explains why it cannot.
              const blocked = (!on && atCap) || (on && selected.length === 1);
              const why = blocked
                ? (on ? 'A pivot needs at least one of these.' : `At most ${maxBreakdowns} breakdowns — remove one to add this.`)
                : null;
              return (
                <label key={i.key} className={`${s.row} ${blocked ? s.rowOff : ''}`}
                       title={why || undefined}>
                  <input type="checkbox" checked={on} disabled={blocked}
                         onChange={() => (isBreak ? onToggleBreakdown(i.key) : onToggleMeasure(i.key))} />
                  <span className={s.rowLabel}>{i.label}</span>
                  {isBreak && on && (
                    <span className={s.order} title="Nesting position">
                      {selectedBreakdowns.indexOf(i.key) + 1}
                    </span>
                  )}
                  <span className={s.rowSpacer} />
                  {(why || help[i.key]) && (
                    <span className={s.dot} tabIndex={0} role="img"
                          aria-label={why || help[i.key]} title={why || help[i.key]}>
                      <Info size={11} aria-hidden="true" />
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        ))}
      </div>

      {footNote && <div className={s.foot}>{footNote}</div>}
    </aside>
  );
}
